import { supabase } from '@/integrations/supabase/client';
import {
  MarketplaceBrand,
  MarketplaceBrandWithStats,
  CreateBrandParams,
  UpdateBrandParams,
  BrandFilters
} from '@/types/marketplace/marketplace';

export class BrandService {
  /**
   * Get all brands with optional filtering
   */
  static async getBrands(filters?: BrandFilters): Promise<MarketplaceBrandWithStats[]> {
    let query = supabase
      .from('marketplace_brands')
      .select('*')
      .order('order_index', { ascending: true })
      .order('name', { ascending: true });

    if (filters?.category_id) {
      query = query.eq('category_id', filters.category_id);
    }

    if (filters?.is_verified !== undefined) {
      query = query.eq('is_verified', filters.is_verified);
    }

    if (filters?.has_genies) {
      query = query.gt('genie_count', 0);
    }

    if (filters?.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  /**
   * Get most recently created brands (for admin preload)
   */
  static async listBrandsRecent(limit: number = 20): Promise<MarketplaceBrandWithStats[]> {
    const { data, error } = await supabase
      .from('marketplace_brands')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Search brands by name with limit (for admin combobox)
   */
  static async searchBrands(query: string, limit: number = 20): Promise<MarketplaceBrandWithStats[]> {
    if (!query.trim()) {
      return this.listBrandsRecent(limit);
    }
    const { data, error } = await supabase
      .from('marketplace_brands')
      .select('*')
      .ilike('name', `%${query}%`)
      .order('name', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Get brands by category slug
   */
  static async getBrandsByCategory(categorySlug: string): Promise<MarketplaceBrandWithStats[]> {
    const { data: category } = await supabase
      .from('marketplace_categories')
      .select('id')
      .eq('slug', categorySlug)
      .single();

    if (!category) return [];

    return this.getBrands({ category_id: category.id });
  }

  /**
   * Get a single brand by slug
   */
  static async getBrandBySlug(slug: string): Promise<MarketplaceBrandWithStats | null> {
    const { data, error } = await supabase
      .from('marketplace_brands')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return data;
  }

  /**
   * Get a single brand by ID
   */
  static async getBrandById(id: string): Promise<MarketplaceBrand | null> {
    const { data, error } = await supabase
      .from('marketplace_brands')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data;
  }

  /**
   * Create a new brand (admin only)
   */
  static async createBrand(params: CreateBrandParams): Promise<MarketplaceBrand> {
    const { data, error } = await supabase
      .from('marketplace_brands')
      .insert(params)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update a brand (admin only)
   */
  static async updateBrand(id: string, params: UpdateBrandParams): Promise<MarketplaceBrand> {
    const { data, error } = await supabase
      .from('marketplace_brands')
      .update(params)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Delete a brand (admin only)
   */
  static async deleteBrand(id: string): Promise<void> {
    const { error } = await supabase
      .from('marketplace_brands')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  /**
   * Get featured/popular brands across all categories
   */
  static async getFeaturedBrands(limit: number = 12): Promise<MarketplaceBrandWithStats[]> {
    const { data, error } = await supabase
      .from('marketplace_brands')
      .select('*')
      .eq('is_verified', true)
      .gt('genie_count', 0)
      .order('genie_count', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }
}
